// File: src/Math/MathLessonPools.js
import {MultiplicationTable, DynamicPizza } from "./MathTools/ElementaryMathHelperTool";
import EquationGenerator from "./MathTools/EquationGenerator.js";
import ProportionBar from "./MathTools/ProportionBar.js";
import CircleTool from "./MathTools/CircleTool.js";
import PolygonTool from "./MathTools/PolygonTool.js";
import TransformationTool from "./MathTools/TransformationTool.js";
import RectPrismTool from "./MathTools/RectPrismTool.js";
import CoinDiceSimulator from "./MathTools/CoinDiceSimluator.js";
import DataAnalyzer from "./MathTools/DataAnalyzer.js";


/** 
 * MathLessonPools.js
 * - Centralized chapterPages for all math grades (Grade 1–12)
 * - Import from each lesson file (e.g., Grade1MathLesson.js) as needed.
 */

export const mathChapterPages = {
    1: [
      {
        title: "Welcome to Grade 1 Math",
        content: (
          <>
            <p>
              Hi there! In this lesson, we will learn about basic math topics:
              Counting, Addition, and Subtraction. Flip through the pages to explore each
              topic!
            </p>
            <p>
              Let’s get started! <span role="img" aria-label="Star">⭐</span>
            </p>
          </>
        ),
      },
      {
        title: "Counting",
        content: (
          <>
            <p>
              Counting means saying numbers in order, like 1, 2, 3, 4, 5. 
              We can also count objects like <span role="img" aria-label="apples">🍎🍎</span> or <span role="img" aria-label="crayons">🖍️</span>!
            </p>
            <p>
              Practice counting things around you: 
              How many <span role="img" aria-label="stars">⭐</span> do you see? 
              Or how many <span role="img" aria-label="hearts">❤️</span> can you draw?
            </p>
          </>
        ),
      },
      {
        title: "Counting Order",
        content: (
          <>
            <p>
              The order we count in is important! We start from 1, then go 2, 3, 4,
              5, and so on. 
            </p>
            <p>
              For example, counting up to 10: 
              <strong>1, 2, 3, 4, 5, 6, 7, 8, 9, 10</strong>. 
              <span role="img" aria-label="ten fingers"> 🙌 </span>
            </p>
            <p>
              Try saying the numbers out loud. Notice how we <strong>never skip</strong> any 
              number in the sequence. 
            </p>
          </>
        ),
      },
      {
        title: "Addition",
        content: (
          <>
            <p>
              Addition is putting numbers together to find a total. For example: 
              <strong>2 + 3 = 5</strong> <span role="img" aria-label="hand">✋</span>.
              Try adding some fun items at home, like pencils or toy cars. 
            </p>
            <p>
              Example: If you have 3 <span role="img" aria-label="apples">🍎</span> and
              2 <span role="img" aria-label="apples">🍎</span>, how many do you have total?
            </p>
          </>
        ),
      },
      {
        title: "Subtraction",
        content: (
          <>
            <p>
              Subtraction is taking away from a bigger number. 
              Example: <strong>5 - 2 = 3</strong>. 
            </p>
            <p>
              You can imagine removing candies from a bag. If you have
              5 <span role="img" aria-label="candy">🍬</span> and eat 2, 
              how many are left?
            </p>
          </>
        ),
      },
      {
        title: "Lesson Summary",
        content: (
          <>
            <p>
              <strong>Great job!</strong> You've finished the lesson pages. 
              Let's take a Quick Quiz to see what you've learned so far.
              <span role="img" aria-label="confetti"> 🎉</span>
            </p>
          </>
        ),
      },
    ],
    2: [
  // ---------- CHAPTER 1 ----------
  {
    chapter: 1,
    title: "Chapter 1 - Page 1: Basic Place Value",
    content: (
      <>
        <p>
          Place value means splitting numbers into tens &amp; ones. 
          Example: 42 = 4 tens (40) + 2 ones (2).
        </p>
        <p>
          This helps us see how large a number is and makes addition or subtraction easier.
        </p>
      </>
    )
  },
  {
    chapter: 1,
    title: "Chapter 1 - Page 2: Addition Basics",
    content: <span>Addition Demo (interactive in lesson)</span>,
    demoType: "placeValueAddition"
  },
  {
    chapter: 1,
    title: "Chapter 1 - Page 3: Column Format Addition & Subtraction",
    content: (
      <>
        <p>When adding multi-digit numbers, we often write them in columns:</p>
        <table className="math-column-table" style={{ marginBottom: 16 }}>
          <tbody>
            <tr><td></td><td>24</td></tr>
            <tr><td className="math-column-operator">+</td><td>12</td></tr>
            <tr><td></td><td className="math-column-line"></td></tr>
            <tr><td></td><td className="math-column-result">36</td></tr>
          </tbody>
        </table>
        <p>Similarly for subtraction, especially if we need to borrow from the tens place:</p>
        <table className="math-column-table">
          <tbody>
            <tr><td></td><td>24</td></tr>
            <tr><td className="math-column-operator">-</td><td>12</td></tr>
            <tr><td></td><td className="math-column-line"></td></tr>
            <tr><td></td><td className="math-column-result">12</td></tr>
          </tbody>
        </table>
      </>
    )
  },
  {
    chapter: 1,
    title: "Chapter 1 - Page 4: Column Format Addition & Subtraction Continue...",
    content: (
      <>
        <p>What happens when you add these?</p>
        <table className="math-column-table" style={{ marginBottom: 10 }}>
          <tbody>
            <tr><td></td><td>26</td></tr>
            <tr><td className="math-column-operator">+</td><td>15</td></tr>
            <tr><td></td><td className="math-column-line"></td></tr>
            <tr><td></td><td className="math-column-result">??</td></tr>
          </tbody>
        </table>
        <p>The 6 + 5 becomes 11, so there's an extra “1” to carry to the tens place...</p>
        <table className="math-column-table" style={{ marginBottom: 10 }}>
          <tbody>
            <tr><td style={{ color: "#2196f3" }}>1</td><td></td></tr>
            <tr><td></td><td>26</td></tr>
            <tr><td className="math-column-operator">+</td><td>15</td></tr>
            <tr><td></td><td className="math-column-line"></td></tr>
            <tr><td></td><td className="math-column-result">41</td></tr>
          </tbody>
        </table>
        <p>
          2 + 1 = 3, plus the carried 1 → total 4 tens, so 41.
        </p>
        <hr />
        <p>
          Similarly for subtraction, especially if borrowing from tens place.
        </p>
        <table className="math-column-table">
          <tbody>
            <tr>
              <td style={{ color: "#2196f3" }}>1</td>
              <td>12</td>
            </tr>
            <tr>
              <td></td>
              <td>22</td>
            </tr>
            <tr>
              <td className="math-column-operator">-</td>
              <td>14</td>
            </tr>
            <tr>
              <td></td>
              <td className="math-column-line"></td>
            </tr>
            <tr>
              <td></td>
              <td className="math-column-result">8</td>
            </tr>
          </tbody>
        </table>
        <p>... 10 - 10 cancels out, 12 - 4 is 8. So total 8 remain.</p>
      </>
    )
  },
  {
    chapter: 1,
    title: "Chapter 1 - Page 5: Intro to Multiplication",
    content: <span>Multiplication Demo (interactive in lesson)</span>,
    demoType: "multiplication"
  },
        // ---------- CHAPTER 2 ----------
        {
          chapter: 2,
          title: "Chapter 2 - Page 1: Finding Patterns",
          content: (
            <>
              <p>
                Patterns can be shapes, numbers, or letters that repeat or grow predictably.
                Example: 2, 4, 6, 8 (adding 2 each time).
              </p>
            </>
          )
        },
        {
          chapter: 2,
          title: "Chapter 2 - Page 2: Algebraic Thinking",
          content: (
            <>
              <p>
                Algebra uses unknown symbols (x or __) to represent missing values.
                Example: 8 + x = 12 → x = 4.
              </p>
            </>
          )
        },
        {
          chapter: 2,
          title: "Chapter 2 - Page 3: More Examples",
          content: (
            <>
              <p>
                If you see 5, 10, 15, 20... the rule is +5 each step. Next would be 25.
              </p>
            </>
          )
        },
    
        // ---------- CHAPTER 3 ----------
        {
          chapter: 3,
          title: "Chapter 3 - Page 1: Measuring Length & Weight",
          content: (
            <>
              <p>
                We use rulers (cm or inches) to measure length, scales (kg or lbs) for weight.
              </p>
              <p>
                Example: A pencil might be 14 cm long, and a book might weigh 1 kg.
              </p>
            </>
          )
        },
        {
          chapter: 3,
          title: "Chapter 3 - Page 2: Temperature & Time",
          content: (
            <>
              <p>
                Time: 60 minutes in an hour, 24 hours in a day. 
                Temperature: 0°C = 32°F, 100°C = 212°F, etc.
              </p>
              <p>
                Example: 20°C is about 68°F, using the formula °F = (°C × 9/5) + 32.
              </p>
            </>
          )
        },
        {
          chapter: 3,
          title: "Chapter 3 - Page 3: Basic Geometry",
          content: <span>Shape Drawing Demo (interactive in lesson)</span>,
          demoType: "shapes"
        },
    
        // ---------- CHAPTER 4 ----------
        {
          chapter: 4,
          title: "Chapter 4 - Page 1: Collecting Data",
          content: (
            <>
              <p>
                Ask questions, record answers (like “favorite fruit?”).
                Then tally results, e.g., apples=4, bananas=3.
              </p>
            </>
          )
        },
        {
          chapter: 4,
          title: "Chapter 4 - Page 2: Bar Graphs & Charts",
          content: (
            <>
              <p>
                A bar graph visually shows how many people chose each fruit.
                The taller the bar, the more popular the choice.
              </p>
            </>
          )
        },
        {
          chapter: 4,
          title: "Chapter 4 - Page 3: Probability Terms",
          content: (
            <>
              <ul>
                <li>Impossible: 0% chance</li>
                <li>Unlikely: less than half chance</li>
                <li>Likely: more than half chance</li>
                <li>Certain: 100% guaranteed</li>
              </ul>
            </>
          )
        },
    
        // ---------- CHAPTER 5 ----------
        {
          chapter: 5,
          title: "Chapter 5 - Page 1: Money Basics",
          content: (
            <>
              <p>
                Coins &amp; bills: e.g., 25¢ = quarter, 10¢ = dime, 100¢ = $1.
              </p>
            </>
          )
        },
        {
          chapter: 5,
          title: "Chapter 5 - Page 2: Saving & Spending",
          content: (
            <>
              <p>
                If you have $5 and need $10, you can wait and save until you reach $10.
              </p>
            </>
          )
        },
        {
          chapter: 5,
          title: "Chapter 5 - Page 3: Making Change",
          content: (
            <>
              <p>
                If you pay $10 for a $6 item, you get $4 back.
                Learn to count coins/bills for correct change.
              </p>
            </>
          )
        },
      ],
    3: [ 
    {
        chapter: 1,
        title: <h1>"Place Value to 1 000"</h1>,
        content: (
          <>
            <p>
              738 → <strong>7 hundreds</strong>, 3 tens, 8 ones.
            </p>
            <p>Value of 7 is 700.</p>
          </>
        ),
      },
      {
        chapter: 1,
        title: <h1>"Add within 1 000"</h1>,
        content: (
          <>
            <strong>
              <pre>{`  312\n+ 147\n─────\n  459`}</pre>
            </strong>
            <h1>"Subtract within 1 000"</h1>
            <strong>
              <pre>{`  312\n− 147\n─────\n  165`}</pre>
            </strong>
          </>
        ),
      },
      {
        chapter: 1,
        title: <h1>"Multiplication: Intro of  X "</h1>,
        content: (
          <>
            <p>4 rows of 5 chairs → 4 × 5 = 20.</p>
            <p>
              There are 10 kids in the class, each one gets 3 candy → 10 × 3 = 30.
            </p>
            <h2>Here is the chart for the Multiplication Table!</h2>
            <MultiplicationTable />
          </>
        ),
      },
      {
        chapter: 1,
        title: <h1>"Division: Intro of  ÷ "</h1>,
        content: (
          <>
            <h1>Division is simply the reverse of the Multiplication</h1>
            <p>
              12 cookies ÷ 4 kids → 3 cookies every kid. Remember: 3 x 4 = 12!
            </p>
            <p>
              20 trees ÷ 5 farms → 4 trees every farm. Remember: 4 x 5 = 20!
            </p>
          </>
        ),
      },
      {
        chapter: 1,
        title: <h1>"The Intro of Fractions"</h1>,
        content: (
          <>
            <p>
              Shade 1 of 4 equal parts → <strong>¼</strong>.
            </p>
            <DynamicPizza />
          </>
        ),
      },
    
      // ---------- CHAPTER 2 ----------
      {
        chapter: 2,
        title: <h1>"Growing & Shrinking Patterns"</h1>,
        content: (
          <p>
            4, 8, 12, 16 … rule is “add 4”. Skip‑count by 2 s, 5 s, 10 s, 25 s!
          </p>
        ),
      },
      {
        chapter: 2,
        title: <h1>"Unknowns & Inequalities"</h1>,
        content: (
          <>
            <p>5 + ___ = 9 → the blank is 4.</p>
            <p>7 + 2 ≠ 10 because 9 ≠ 10.</p>
          </>
        ),
      },
    
      // ---------- CHAPTER 3 ----------
      {
        chapter: 3,
        title: <h1>"Length"</h1>,
        content: (
          <ul>
            <li>1 m = 100 cm | 1cm = 10mm</li>
            <li>Is 17 cm pencil longer than your hand?</li>
          </ul>
        ),
      },
      {
        chapter: 3,
        title: <h1>"Mass"</h1>,
        content: (
          <ul>
            <li>1 kg = 1 000 g | 1g = 1000mg</li>
            <li>Is 17 cm pencil longer than your hand?</li>
          </ul>
        ),
      },
      {
        chapter: 3,
        title: <h1>"Volume"</h1>,
        content: (
          <ul>
            <li>1 L = 1000 ml </li>
            <li>Is 17 cm pencil longer than your hand?</li>
          </ul>
        ),
      },
      {
        chapter: 3,
        title: <h1>"Telling Time"</h1>,
        content: (
          <>
            <p>Clock to nearest 5 min. 8 : 30 → “half past 8”.</p>
          </>
        ),
      },
      {
        chapter: 3,
        title: <h1>"Telling Perimeter"</h1>,
        content: (
          <>
            <p>
              Rectangle 5 cm × 3 cm → perimeter (5+3)×2 = 16 cm. (5 + 3 + 5 + 3)
              <pre>{`
                 5        
         _______
        3  |______| 3
          5
               `}</pre>
            </p>
          </>
        ),
      },
    
      // ---------- CHAPTER 4 ----------
      {
        chapter: 4,
        title: <h1>"Turn, Flip & Slide!"</h1>,
        content: (
          <ul>
            <li>
              <strong>Turn:</strong> To turn something means to rotate it around a point. Imagine spinning a wheel or turning your head to look behind you!
            </li>
            <li>
              <strong>Flip:</strong> Flipping is like turning something over to show the other side. For example, when you flip a pancake or a page in a book, you see its mirror image.
            </li>
            <li>
              <strong>Slide:</strong> To slide means to move an object from one spot to another without rotating it. Think about sliding a book across a table.
            </li>
          </ul>
        ),
      },
      {
        chapter: 4,
        title: <h1>"Exploring 2D & 3D Shapes"</h1>,
        content: (
          <>
            <h2>2D Shapes (Flat Shapes)</h2>
            <ul>
              <li>
                <strong>Triangle</strong> – A shape with 3 sides and 3 angles.
              </li>
              <li>
                <strong>Square</strong> – A four-sided shape with all sides equal and every angle a right angle.
              </li>
              <li>
                <strong>Rectangle</strong> – A four-sided shape with opposite sides equal, and all angles 90°.
              </li>
              <li>
                <strong>Pentagon</strong> – A polygon with 5 straight sides.
              </li>
              <li>
                <strong>Hexagon</strong> – A polygon with 6 straight sides. (Example: a honeycomb cell)
              </li>
              <li>
                <strong>Octagon</strong> – A shape with 8 straight sides, like a stop sign.
              </li>
              <li>
                <strong>Circle</strong> – A round shape with no straight sides or angles.
              </li>
            </ul>
            <h2>3D Shapes (Solid Shapes)</h2>
            <ul>
              <li>
                <strong>Cube</strong> – A solid with 6 square faces, 12 edges, and 8 vertices.
              </li>
              <li>
                <strong>Rectangular Prism (Cuboid)</strong> – Similar to a cube, but the faces are rectangles and not all sides are the same.
              </li>
              <li>
                <strong>Pyramid</strong> – A solid with a polygonal base and triangular faces that meet at a single point (the apex). A common example is a square pyramid.
              </li>
              <li>
                <strong>Triangular Prism</strong> – A solid with two parallel triangular bases and rectangular sides connecting them.
              </li>
              <li>
                <strong>Cylinder</strong> – A solid with two circular bases and a curved surface.
              </li>
              <li>
                <strong>Cone</strong> – A solid with a circular base that tapers smoothly to a point.
              </li>
              <li>
                <strong>Sphere</strong> – A perfectly round solid shape; every point on the surface is the same distance from the center.
              </li>
            </ul>
          </>
        ),
      },
      {
        chapter: 4,
        title: <h1>"Shape‑Drawing Demo"</h1>,
        content: (
          <p>
            Use the buttons below to cycle through common 2D and 3D shapes.
          </p>
        ),
        isShapeDemo: true
      },
    
      // ---------- CHAPTER 5 ----------
      {
        chapter: 5,
        title: <h1>"Collecting & Graphing Data"</h1>,
        content: (
          <>
            <p>
              Survey 10 classmates’ favourite pets (for example: dog, cat, fish, or bird).
            </p>
            <p>Record how many times each pet is chosen.</p>
            <p>Create a bar graph to show the results.</p>
            <p>
              The graph shows each pet on the horizontal axis and the number of votes on the vertical axis.
            </p>
          </>
        ),
        isGraphDemo: true, // for lesson to use InteractiveGraphSection
      },
      {
        chapter: 5,
        title: <h1>"Probability Words"</h1>,
        content: (
          <ul>
            <li>
              <strong>Certain</strong> – It will happen every time (100%).
            </li>
            <li>
              <strong>Likely</strong> – Expected to happen more than half the time.
            </li>
            <li>
              <strong>Unlikely</strong> – Not expected to happen most of the time.
            </li>
            <li>
              <strong>Impossible</strong> – It cannot happen (0%).
            </li>
          </ul>
        ),
      },
    
      // ---------- CHAPTER 6 ----------
      {
        chapter: 6,
        title: <h1>"Coins, Bills & Making Change"</h1>,
        content: (
          <p>
            Buy a toy for $3.75 with a $5 bill → change is $1.25.
          </p>
        ),
      },
      {
        chapter: 6,
        title: <h1>"Adding / Subtracting Money"</h1>,
        content: <p>$1.25 + $0.50 = $1.75 | $5 − $2.60 = $2.40</p>,
      },
      {
        chapter: 6,
        title: <h1>"Saving Goals"</h1>,
        content: <p>Save $3 per week for a $12 game → 4 weeks.</p>,
      },
    ],
    4: [
        // ---------- CHAPTER 1 ----------
        {
          title: <h1>"Place Value to 10 000"</h1>,
          content: (
            <>
              <p>
                <strong>Key Grade 4 ideas:</strong> We handle thousands and
                ten-thousands. E.g. “12,345” = 1 ten-thousand (10,000)
                + 2 thousands (2,000) + 3 hundreds (300) + 4 tens (40) + 5 ones (5).
              </p>
            </>
          ),
        },
        {
          title: <h1>"Multi-Digit Addition &amp; Subtraction"</h1>,
          content: (
            <>
              <p>Adding or subtracting 4-digit numbers, watch for carrying/borrowing:</p>
              <pre><strong>{`
        5,432
      + 2,189
      ------
        7,621
      `}</strong></pre>
            </>
          ),
        },
        {
          title: <h1>"Multiplication &amp; Division (2-digit)"</h1>,
          content: (
            <>
              <p>
                Up to 12×12 tables, 2-digit × 2-digit (like 25 × 32), and short division with or without remainders:
              </p>
              <pre>{`
           25
         x 32
         ----
           50   (25 × 2)
       + 750   (25 × 30)
         ----
          800
      `}</pre>
            </>
          ),
        },
        {
          title: <h1>"Decimals &amp; Fractions"</h1>,
          content: (
            <>
              <ul>
                <li>Decimals up to hundredths (0.01): 1.25, 0.62, etc.</li>
                <li>Compare decimals: 0.5 vs 0.55 → 0.55 is bigger</li>
                <li>Fractions: 3/4, 2/3, 5/12, etc. Convert to decimals or compare directly</li>
              </ul>
            </>
          ),
        },
        {
          title: <h1>"Fraction Operations"</h1>,
          content: (
            <>
              <p>
                <strong>1) Adding Fractions (Like Denominators)</strong>
              </p>
              <p>
                When denominators match, just add the numerators:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
             2     1     3
             -  +  -  =  -
             5     5     5
          `}</pre>
              <p>
                Similarly for subtraction:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
             3     1     2
             -  -  -  =  -
             7     7     7
          `}</pre>
      
              <p>
                <strong>2) Adding Fractions (Unlike Denominators)</strong>
              </p>
              <p>
                First find a common denominator. For example, 1/2 + 1/3:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
          We rewrite each fraction with denominator 6:
      
              1     3
              -  =  -
              2     6
      
            + 1     2
              -  =  -
              3     6
          ------------
              5
              -
              6
          `}</pre>
              <p>
                Subtraction is similar: 3/4 - 1/6 → common denominator 12:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
            3      9
            -  =   --
            4     12
      
          - 1      2
            -  =   --
            6     12
          ----------
            7
            --
           12
          `}</pre>
      
              <p>
                <strong>3) Multiplying Fractions</strong>
              </p>
              <p>
                Multiply top × top and bottom × bottom:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
             2     3     6     1
             -  ×  -  =  --  =  -
             3     4    12     2
          `}</pre>
              <p>
                Multiplying a fraction by a whole number (3 × 1/4 → treat 3 as 3/1):
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
            3   3    3
            - = - ×  -
            1   1    4
      
             3
             -
             4
          `}</pre>
      
              <p>
                <strong>4) Dividing Fractions</strong>
              </p>
              <p>
                Multiply by the <em>reciprocal</em> (flip) of the second fraction:
              </p>
              <pre style={{ lineHeight: "1.4" }}>{`
            3      3      2      6
            -  ÷   -   =   -  ×   -  =  --
            4      1      1      1     4
      
          = 6/4 = 3/2 = 1 1/2
          `}</pre>
              <p>
                After each operation, you can simplify/reduce the fraction if needed.
              </p>
            </>
          ),
        },
        {
          title: <h1>"Mixed Numbers"</h1>,
          content: (
            <>
              <p>A mixed number, such as 2 1/3, can be converted to an improper fraction for easier multiplication or comparison.</p>
              <pre style={{ lineHeight: "1.4" }}>{`
                2 1/3
          = ( (2 × 3) + 1 )
            ---------------
                   3
      
          = 7/3
          `}</pre>
              <p>This way, 2 1/3 becomes 7/3, making it straightforward to multiply or compare with other fractions.</p>
            </>
          ),
        },
      
        // ---------- CHAPTER 2 ----------
        {
          title: <h1>"Patterns &amp; Algebra (Overview)"</h1>,
          content: (
            <>
              <p>
                <strong>Grade 4 expansions:</strong> We handle 2-step patterns (+4, ×2),
                factor/multiple knowledge to 12×12, variables in simple equations (2y-3=15).
              </p>
            </>
          ),
        },
        {
          title: <h1>"Patterns &amp; Algebra Examples"</h1>,
          content: (
            <>
              <ul>
                <li>2-step pattern: 2, 6, 12, 16, 32... (+4, then ×2)</li>
                <li>Variable: 2y-3=15 → y=9</li>
                <li>Multiples of 4: 4,8,12,16,20,24,28,32,36,40...</li>
                <li>Factors of 24: 1,2,3,4,6,8,12,24</li>
              </ul>
            </>
          ),
        },
      
        // ---------- CHAPTER 3 ----------
        {
          title: <h1>"Measurement (Overview)"</h1>,
          content: (
            <>
              <p>
                In Grade 4, we build on basic measurement skills:
              </p>
              <ul>
                <li>
                  <strong>Length:</strong> You already know 1 km = 1,000 m, and 1 m = 100 cm.
                  Now we practice measuring distances in kilometres and metres (e.g.,
                  the length of a park trail or the height of a building).
                </li>
                <li>
                  <strong>Mass:</strong> We measure larger masses in kilograms (kg)
                  and smaller in grams (g). For example, a backpack might weigh 4 kg,
                  while a pencil weighs only a few grams.
                </li>
                <li>
                  <strong>Volume/Capacity:</strong> We use litres (L) and millilitres (mL)
                  to measure how much liquid a container can hold. E.g.,
                  a water bottle is 500 mL, while a big jug is 2 L.
                </li>
                <li>
                  <strong>Perimeter &amp; Area:</strong> We measure the boundary (perimeter)
                  and the surface (area) of rectangles and other polygons.
                  E.g., a 5 m by 3 m rectangle has perimeter 16 m and area 15 m².
                </li>
                <li>
                  <strong>Complex Shapes:</strong> We break an L‑shaped or composite figure
                  into simpler rectangles to find its total area.
                </li>
                <li>
                  <strong>Time:</strong> We can handle elapsed time across multiple hours or
                  crossing noon/midnight (AM/PM).
                </li>
              </ul>
            </>
          ),
        },
        {
          title: <h1>"Measurement Examples"</h1>,
          content: (
            <>
              <p>
                Let’s see some detailed examples of conversions, perimeter/area, and L‑shaped figures.
              </p>
              <ul>
                <li>
                  <strong>Conversions:</strong>
                  <br />
                  1) 3 km = 3 × 1,000 m = 3,000 m
                  <br />
                  2) 500 cm = 500 × 10 mm = 5,000 mm
                  <br />
                  3) 2 kg = 2 × 1,000 g = 2,000 g
                </li>
                <li>
                  <strong>Perimeter of a Rectangle:</strong>
                  <br />
                  A 7 m by 4 m rectangle has perimeter = 2 × (7 + 4) = 22 m
                </li>
                <li>
                  <strong>Area of a Rectangle:</strong>
                  <br />
                  The same 7 m by 4 m rectangle has area = 7 × 4 = 28 m²
                </li>
                <li>
                  <strong>L‑Shaped (Composite) Figure:</strong>
                  <br />
                  If the figure can be split into two smaller rectangles, find each rectangle’s area, then add them. For example:
                  <pre style={{ lineHeight: "1.3" }}>{`
           +-----5m-----+--3m--+
           |            |      |
           |     A      |   B  |  ← 3m tall
           +------------+------+
           |(entire width = 8m)| ← 4m tall
           +-------------------+
      
        `}</pre>
                  <p>
                    Here, the <strong>Rectangle A</strong> is 5 m × 3 m = 15 m².<br />
                    the <strong>Rectangle B</strong> is 3 m × 3 m = 9 m².<br />
                    The <strong>bottom rectangle</strong> is 8 m × 4 m = 32 m².<br />
                    Total area = 15 + 32 + 9 = 56 m².
                  </p>
                </li>
              </ul>
            </>
          ),
        },
        {
          title: <h1>"Telling & Calculating Time"</h1>,
          content: (
            <>
              <p>Grade 4 expands your ability to handle time problems, including crossing noon/midnight or multi-hour spans.</p>
              <ul>
                <li>
                  <strong>Elapsed Time Example 1:</strong> If an event starts at 9:15 AM and ends at 11:05 AM,
                  total duration is 1 hour 50 minutes.
                </li>
                <li>
                  <strong>Crossing Noon:</strong> Suppose a show starts at 11:40 AM and runs for
                  1 hour 30 minutes → it ends at 1:10 PM.
                </li>
                <li>
                  <strong>Crossing Midnight (24-hour format):</strong> A flight leaves 11:20 PM
                  (23:20 in 24-hour) and lasts 2 hours → arrival 1:20 AM the next day (01:20).
                </li>
              </ul>
      
              <p>
                We can calculate these by adding the hours/minutes step by step or converting everything to
                minutes, then converting back. Practice helps you master these real-life scenarios!
              </p>
      
              {/* Optional: Add your InteractiveClock component here */}
              {/* <h2>Try Setting the Clock Yourself:</h2>
              <InteractiveClock /> */}
            </>
          ),
        },
      
        // ---------- CHAPTER 4 ----------
        {
          title: <h1>"Geometry (Overview)"</h1>,
          content: (
            <>
              <p>
                In Grade 4, we explore angles, triangles, lines, symmetry, and coordinate grids.
                Here are some key concepts:
              </p>
              <ul>
                <li>
                  <strong>Angles:</strong> acute (&lt;90°), right (90°), obtuse (&gt;90°), straight (180°).
                  A reflex angle is &gt;180° but &lt;360°.
                </li>
                <li>
                  <strong>Triangles:</strong>
                  <ul>
                    <li>
                      <em>By sides:</em> equilateral (all sides equal), isosceles (two sides equal), scalene (all sides different).
                    </li>
                    <li>
                      <em>By angles:</em>
                      – A <strong>right triangle</strong> has one 90° angle (e.g., angles 90°, 45°, 45°).
                      – An <strong>acute triangle</strong> has all angles &lt;90°.
                      – An <strong>obtuse triangle</strong> has one angle &gt;90°.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Lines:</strong> parallel lines never meet (like railroad tracks),
                  perpendicular lines form a 90° angle.
                  Intersecting lines cross at a point.
                </li>
                <li>
                  <strong>Line Symmetry:</strong> A shape is symmetrical if it can be folded along a line so that both halves match.
                </li>
                <li>
                  <strong>Coordinate Grids:</strong> A grid with x-axis (horizontal) and y-axis (vertical).
                  A point like (3,2) means move 3 steps right, 2 steps up from the origin (0,0).
                </li>
              </ul>
            </>
          ),
        },
      
        {
          title: <h1>"Further Geometry Examples"</h1>,
          content: (
            <>
              <p>Here are some illustrative ASCII examples:</p>
              <ul>
                <li>
                  <strong>Rectangle Symmetry:</strong> A rectangle (not a square) has 2 lines of symmetry:
                  one horizontal center line, one vertical center line.
                  <pre style={{ lineHeight: "1.3" }}>{`
           +---------+
           |         |
           |         |
           |---------|  <- horizontal line
           |         |
           |         |
           +---------+
           ^         ^
           |         |
         vertical center line
        (through the middle)
        `}</pre>
                </li>
      
                <li>
                  <strong>Square Symmetry:</strong> A square has 4 lines of symmetry (2 diagonals + horizontal & vertical).
                  <pre style={{ lineHeight: "1.3" }}>{`
             +-----+
             |     |
             |  X  |  <--- diagonals cross center 'X'
             |     |
             +-----+
      
        (1) vertical center
        (2) horizontal center
        (3) diagonal \\
        (4) diagonal /
        `}</pre>
                </li>
      
                <li>
                  <strong>Parallel Lines:</strong> Parallel lines never meet:
                  <pre style={{ lineHeight: "1.3" }}>{`
        |   |
        |   |
        |   |
        |   |
        (same distance apart forever)
        `}</pre>
                </li>
      
                <li>
                  <strong>Perpendicular Lines:</strong> Perpendicular lines cross at a 90° angle:
                  <pre style={{ lineHeight: "1.3" }}>{`
        |
        |   <--- 90° angle
        +-------
        `}</pre>
                </li>
              </ul>
            </>
          ),
        },
      
        {
          title: <h1>"Coordinate Grid Practice"</h1>,
          content: (
            <>
              <p>
                Plotting points on a grid: (3,2) means x=3, y=2. Move 3 steps right from
                (0,0), 2 steps up, and place a dot. Try it yourself below!
              </p>
              {/* Optional: Add your InteractiveGrid component here */}
              {/* <InteractiveGrid /> */}
            </>
          ),
          isGridDemo: true,  // So you can conditionally render <InteractiveGrid />
        },
      
        {
          title: <h1>"Shape‑Drawing Demo"</h1>,
          content: (
            <p>Use the buttons below to cycle through advanced 2D and 3D shapes!</p>
          ),
          isShapeDemo: true,
        },
      
        // ---------- CHAPTER 5 ----------
        {
          title: <h1>"Data &amp; Probability (Overview)"</h1>,
          content: (
            <>
              <p>
                We create bar graphs, line plots, or pictographs to show data,
                and interpret them (which is largest/smallest?).
                We also use probability words: certain, impossible, likely,
                unlikely, equally likely, etc.
              </p>
            </>
          ),
        },
        {
          title: <h1>"Data &amp; Probability Examples"</h1>,
          content: (
            <>
              <ul>
                <li>Create a line plot for the number of pets each student has</li>
                <li>Bar graph: apples=12, oranges=8 → apples is bigger</li>
                <li>Rolling a die: rolling a 6 has probability 1/6 → “unlikely”</li>
                <li>Flipping a fair coin → heads or tails are “equally likely”</li>
              </ul>
            </>
          ),
          isGraphDemo: true,
        },
      
        // ---------- CHAPTER 6 ----------
        {
          title: <h1>"Financial Literacy (Overview)"</h1>,
          content: (
            <>
              <p>
                Estimate, count, compare money up to $100.
                Make change (e.g., paying $50 for a $33.75 item → $16.25 back).
                Weekly saving, budgeting, comparing store prices, etc.
              </p>
            </>
          ),
        },
        {
          title: <h1>"Financial Literacy Examples"</h1>,
          content: (
            <>
              <ul>
                <li>Pay $20 for a $13.45 item → $6.55 change</li>
                <li>Add $12.25 + $3.75 + $5.00 → $21.00 total</li>
                <li>Save $5/week for $30 → 6 weeks</li>
                <li>Compare costs: $9.50 vs $10.20, difference= $0.70</li>
              </ul>
            </>
          ),
        },
      ],
    5: [
        // ---------- CHAPTER 1 ----------
        {
          title: <h1>Welcome to Grade 5</h1>,
          content: (
            <>
              <p>
                Welcome to Grade 5 math! This year, we’ll learn about large numbers,
                multi-digit operations, fractions, decimals, geometry, and more.
                Let’s get started!
              </p>
              <p>
                Use the <strong>Next</strong> button to move through the pages.
                Try any interactive tools you see. Have fun exploring!
              </p>
            </>
          ),
        },
        {
          title: <h1>Place Value up to Millions</h1>,
          content: (
            <>
              <p>
                Grade 5 often involves numbers up to <strong>1,000,000</strong>.
                Each digit’s place is 10 times bigger than the place to its right.
              </p>
              <p>
                For example, in <strong>534,217</strong>:
              </p>
              <ul>
                <li>5 → hundred-thousands place (500,000)</li>
                <li>3 → ten-thousands place (30,000)</li>
                <li>4 → thousands place (4,000)</li>
                <li>2 → hundreds place (200)</li>
                <li>1 → tens place (10)</li>
                <li>7 → ones place (7)</li>
              </ul>
            </>
          ),
        },
        {
          title: <h1>Comparing & Rounding Large Numbers</h1>,
          content: (
            <>
              <p>
                To compare big numbers, start at the leftmost digit and go right
                until you find a difference. For example: 2,345,789 vs. 2,376,001.
              </p>
              <p>
                <strong>Rounding</strong> helps estimate. For instance, rounding 42,736 to the nearest
                thousand gives 43,000, while rounding to the nearest ten thousand
                gives 40,000. If you want a quick check, estimation can tell you
                whether your final answer is reasonable.
              </p>
            </>
          ),
        },
        {
          title: <h1>Multi-Digit Multiplication</h1>,
          content: (
            <>
              <p>
                You might multiply 2-, 3-, or 4-digit numbers. Using the standard
                algorithm:
              </p>
              <pre style={{ lineHeight: "1.3" }}>
      {`   2,347
      x     56
      -----------
         14,082   (2,347 × 6)
      + 112,336   (2,347 × 50)
      -----------
        126,418`}
              </pre>
              <p>
                Be sure to carry digits correctly. Estimation (like 2,300 × 50 ≈
                115,000) can help confirm your answer’s in the right range.
              </p>
            </>
          ),
        },
        {
          title: <h1>Long Division & Remainders</h1>,
          content: (
            <>
              <p>
                You’ll extend <strong>long division</strong> to bigger divisors
                (2–3 digits). Sometimes, you’ll get a remainder. For example:
              </p>
              <pre style={{ lineHeight: "1.3" }}>
      {`24 | 12,480
          - 12,0
            48
            - 48
             0  (no remainder)`}
              </pre>
              <p>
                Estimate first: 12,480 ÷ 24 is close to 12,500 ÷ 25 ≈ 500.
                If your actual answer is around 520 or 480, you know you’re close.
              </p>
            </>
          ),
        },
      
        // ---------- CHAPTER 2 ----------
        {
          title: <h1>Fractions: Deeper Addition & Subtraction</h1>,
          content: (
            <>
              <p>
                We add or subtract fractions by finding a common denominator (often
                the LCM). Convert each fraction, then add/subtract the numerators.
              </p>
              <p>
                Example: 1 3/8 + 2 1/4:
              </p>
              <pre style={{ lineHeight: "1.3" }}>
      {`1 3/8 = 11/8
      2 1/4 = 9/4 = 18/8
      -----------
      11/8 + 18/8 = 29/8 = 3 5/8`}
              </pre>
              <p>
                Check if the result simplifies or needs to be written as a mixed
                number.
              </p>
            </>
          ),
        },
        {
          title: <h1>Fractions: Multiplying Fractions</h1>,
          content: (
            <>
              <p>
                To multiply fractions, multiply the numerators, multiply the
                denominators, then simplify. Example: (2/3) × (3/4) = 6/12 = 1/2.
              </p>
              <p>
                For <strong>mixed numbers</strong>, convert each to an improper
                fraction first. E.g., 2 1/2 = 5/2.
              </p>
            </>
          ),
        },
        {
          title: <h1>Fractions: Multiplying Mixed Numbers</h1>,
          content: (
            <>
              <p>
                Example: 2 1/2 × 3 1/3:
              </p>
              <pre style={{ lineHeight: "1.3" }}>
      {`2 1/2 = 5/2
      3 1/3 = 10/3
      (5/2) × (10/3) = 50/6 = 25/3 = 8 1/3`}
              </pre>
              <p>
                Convert the final product back to a mixed number if needed. 
                Practice for real-life tasks like cooking or measuring materials.
              </p>
            </>
          ),
        },
        {
          title: <h1>Dividing Fractions</h1>,
          content: (
            <>
              <p>
                Some Grade 5 curricula do fraction division now; others wait until
                Grade 6. The key rule: <strong>divide by a fraction = multiply by its reciprocal</strong>.
              </p>
              <p>Example: 2/3 ÷ 1/4 = (2/3) × (4/1) = 8/3 = 2 2/3.</p>
              <p>
                If dividing by a whole number like 5, think of it as 5/1; then
                multiply by 1/5.
              </p>
            </>
          ),
        },
      
        // ---------- CHAPTER 3 ----------
        {
          title: <h1>Decimals & Conversions</h1>,
          content: (
            <>
              <p>
                Decimals are another way to show fractions. E.g., 0.75 = 75% = 3/4.
                We often handle decimals up to thousandths (0.123).
              </p>
              <ul>
                <li>Adding & subtracting decimals (align decimal points!)</li>
                <li>Multiplying decimals (count total decimal places)</li>
                <li>Dividing by 1-digit or 2-digit numbers</li>
              </ul>
              <p>
                We use decimals for money, measurements (like 1.25 L),
                or geometry calculations.
              </p>
            </>
          ),
        },
        {
          title: <h1>Percentages in Depth</h1>,
          content: (
            <>
              <p>
                A percentage means “out of 100.” For instance, 25% = 25/100 = 0.25.
                We often convert between fractions, decimals, and percentages.
              </p>
              <p>
                Example: <strong>What is 20% of 50?</strong>
                It’s 0.20 × 50 = 10.
              </p>
              <p>
                Below is an interactive pie chart—move the slider to see how
                a percentage fills the circle.
              </p>
            </>
          ),
          isPercentageInteractive: true,
        },
      
        // ---------- CHAPTER 4 ----------
        {
          title: <h1>Measurement & Unit Conversions</h1>,
          content: (
            <>
              <p>
                The <strong>metric system</strong> uses units like km, m, cm, mm
                (length), kg, g (mass), and L, mL (volume).
              </p>
              <ul>
                <li>1 km = 1000 m, 1 m = 100 cm, 1 cm = 10 mm</li>
                <li>1 kg = 1000 g, 1 L = 1000 mL</li>
              </ul>
              <p>
                Watch the units. For example, 3.5 km = 3500 m,
                250 mL = 0.25 L, etc.
              </p>
            </>
          ),
        },
        {
          title: <h1>Volume of Prisms & Cubes</h1>,
          content: (
            <>
              <p>
                Volume is measured in cubic units (cm³, m³, etc.).
                For a rectangular prism, volume = length × width × height.
              </p>
              <ul>
                <li>A cube has all edges the same length.</li>
                <li>Doubling an edge multiplies the volume by 8.</li>
              </ul>
              <p>Try changing the cube size below to see how volume changes!</p>
            </>
          ),
          isVolumeInteractive: true,
        },
      
        // ---------- CHAPTER 5 ----------
        {
          title: <h1>Coordinate Geometry Basics</h1>,
          content: (
            <>
              <p>
                On a coordinate plane, the x-axis and y-axis intersect at (0,0).
                A point is (x,y). Quadrants are I, II, III, IV.
              </p>
              <ul>
                <li>Quadrant I: x &gt; 0, y &gt; 0</li>
                <li>Quadrant II: x &lt; 0, y &gt; 0</li>
                <li>Quadrant III: x &lt; 0, y &lt; 0</li>
                <li>Quadrant IV: x &gt; 0, y &lt; 0</li>
              </ul>
              <p>
                Next, you’ll see a coordinate grid you can click to plot points.
              </p>
            </>
          ),
        },
        {
          title: <h1>Plotting Points on a Grid</h1>,
          content: (
            <>
              <p>
                Here’s a 2D grid. Click anywhere, and a red dot appears where you
                clicked. Try negative or positive x,y to see which quadrant it’s in.
              </p>
            </>
          ),
          isCoordinateInteractive: true,
        },
        {
          title: <h1>Data & Graphing</h1>,
          content: (
            <>
              <p>
                You also learn to collect and display data. For example:
              </p>
              <ul>
                <li>Bar graphs, line graphs, or pictographs</li>
                <li>Circle (pie) charts for percentages of a whole</li>
                <li>Basic mean and median for data sets</li>
                <li>Probability (like rolling dice) → 1/6 chance for any face</li>
              </ul>
              <p>
                Graphing and data skills let you compare information quickly and
                make predictions.
              </p>
            </>
          ),
        },
      
        // ---------- CHAPTER 6 ----------
        {
          title: <h1>Lesson Summary</h1>,
          content: (
            <>
              <p>
                Great job! We covered large numbers, fractions, decimals/percents,
                measurement, volume, coordinate geometry, and data.
              </p>
              <p>
                You’re done with the main chapters. Click “Take Chapter 6 Quiz”
                to finish up, then you’ll unlock the final quiz for everything
                in Grade 5.
              </p>
            </>
          ),
        },
      ],
    6: {
        // ---- CHAPTER 1: RATIOS ----
        1: [
          {
            title: <h1>What is a Ratio?</h1>,
            content: (
              <>
                <p>
                  A <strong>ratio</strong> compares two or more quantities by showing how many times one value contains or is contained within the other.
                </p>
                <p>
                  Example: If there are 8 boys and 12 girls in a class, the ratio of boys to girls is 8:12, which simplifies to 2:3.
                </p>
                <p>
                  Ratios can be written as 2 to 3, 2:3, or 2/3 depending on context.
                </p>
              </>
            ),
          },
          {
            title: <h1>Simplifying and Using Ratios</h1>,
            content: (
              <>
                <p>
                  To simplify a ratio, divide both numbers by their greatest common divisor (GCD). Example: 15:10 → both divisible by 5 → 3:2.
                </p>
                <p>
                  If a bag has 12 red and 18 blue marbles, what’s the simplest ratio of red to blue?
                </p>
                <p>
                  <strong>Application:</strong> If a recipe calls for a ratio of 2 cups flour to 3 cups water, how much flour is needed for 9 cups water?
                </p>
              </>
            ),
          },
          {
            title: <h1>Interactive Ratio Bar</h1>,
            content: (
              <>
                <p>
                  Below is an interactive ratio bar. Adjust the numbers to visualize ratios!
                </p>
                <p>
                  <strong>Real-World Example:</strong> Mixing paint, making fruit punch, or building scale models all use ratios.
                </p>
              </>
            ),
            isRatioInteractive: true,
          },
        ],
        // ---- CHAPTER 2: ALGEBRA ----
        2: [
          {
            title: <h1>Understanding Algebraic Equations</h1>,
            content: (
              <>
                <p>
                  An <strong>algebraic equation</strong> uses letters to represent unknowns. The goal is to find their value.
                </p>
                <p>
                  Example: <strong>2x + 4 = 12</strong>. First subtract 4 from both sides, then divide by 2 to solve for x.
                </p>
              </>
            ),
          },
          {
            title: <h1>Solving Step-by-Step</h1>,
            content: (
              <>
                <p>
                  <strong>Step 1:</strong> Isolate the term with x on one side.<br/>
                  <strong>Step 2:</strong> Get x alone by dividing or multiplying.
                </p>
                <p>
                  <strong>Practice:</strong> What is x in 3x – 7 = 11?
                </p>
              </>
            ),
          },
          {
            title: <h1>Common Mistakes & Real Uses</h1>,
            content: (
              <>
                <p>
                  <strong>Be careful</strong> with negatives: <em>-2(x - 5) = 8</em> means you distribute -2 first!
                </p>
                <p>
                  <strong>Everyday Algebra:</strong> Budgeting, figuring out costs, and planning trips all use algebra!
                </p>
                <p>
                  Try a random equation below.
                </p>
              </>
            ),
            isAlgebraInteractive: true,
          },
        ],
        // ---- CHAPTER 3: INTEGERS & NUMBER LINE ----
        3: [
          {
            title: <h1>Integers: Positive and Negative Numbers</h1>,
            content: (
              <>
                <p>
                  <strong>Integers</strong> are whole numbers (positive, negative, or zero).
                </p>
                <p>
                  Example: -3, 0, and 17 are all integers.
                </p>
              </>
            ),
          },
          {
            title: <h1>Number Line & Integer Operations</h1>,
            content: (
              <>
                <p>
                  Integers can be added or subtracted on a number line. <br/>
                  Example: -2 + 5 = 3.
                </p>
                <p>
                  Going left means subtract, right means add!
                </p>
                <p>
                  <strong>Try it below:</strong> Change the numbers to see the result on the line.
                </p>
              </>
            ),
            isIntegerInteractive: true,
          },
          {
            title: <h1>Where Integers Matter</h1>,
            content: (
              <>
                <p>
                  Integers are used for tracking temperatures, financial gains/losses, and altitude (above/below sea level).
                </p>
                <p>
                  <strong>Looking ahead:</strong> Negative exponents, vectors, and programming all use integers!
                </p>
              </>
            ),
          },
        ],
        // ---- CHAPTER 4: DECIMALS & PERCENT ----
        4: [
          {
            title: <h1>Decimals: Understanding Parts of a Whole</h1>,
            content: (
              <>
                <p>
                  A <strong>decimal</strong> shows numbers between whole numbers. Example: 0.25, 2.5, 0.75.
                </p>
                <p>
                  <strong>Tip:</strong> Always align decimal points when adding or subtracting decimals!
                </p>
              </>
            ),
          },
          {
            title: <h1>Percentages & Real-World Uses</h1>,
            content: (
              <>
                <p>
                  <strong>Percent</strong> means “per hundred.” 25% = 25/100 = 0.25. 
                </p>
                <p>
                  <strong>Real-life:</strong> Discounts, grades, interest rates—all use percentages.
                </p>
                <p>
                  <strong>Practice:</strong> What is 15% of 60? (0.15 × 60 = 9)
                </p>
              </>
            ),
          },
          {
            title: <h1>Decimals, Percents, and Conversions</h1>,
            content: (
              <>
                <p>
                  Converting between decimals and percent: Move the decimal two places.<br/>
                  0.75 = 75%, 1.0 = 100%, 0.06 = 6%
                </p>
                <p>
                  <strong>Watch Out:</strong> 0.05 is only 5%, not 50%!
                </p>
              </>
            ),
          },
        ],
        // ---- CHAPTER 5: GEOMETRY & MEASUREMENT ----
        5: [
          {
            title: <h1>Geometry: Area and Perimeter</h1>,
            content: (
              <>
                <p>
                  <strong>Area</strong> = length × width (in square units). <br/>
                  <strong>Perimeter</strong> = sum of all sides. <br/>
                  Rectangle example: 4cm × 6cm → Area = 24cm², Perimeter = 20cm.
                </p>
                <p>
                  <strong>Try:</strong> What’s the area of a 7m × 3m rectangle?
                </p>
              </>
            ),
          },
          {
            title: <h1>Volume and Angles</h1>,
            content: (
              <>
                <p>
                  <strong>Volume</strong> = length × width × height (for rectangular prisms). <br/>
                  <strong>Angles:</strong> Right = 90°, Straight = 180°, Full circle = 360°
                </p>
                <p>
                  <strong>Activity:</strong> Find the angle between the hands of a clock at 3 o’clock (90°).
                </p>
              </>
            ),
          },
          {
            title: <h1>Geometry in the World</h1>,
            content: (
              <>
                <p>
                  Geometry is everywhere: in architecture, art, nature, and sports!
                </p>
                <p>
                  <strong>Looking ahead:</strong> You’ll use area, perimeter, and volume in advanced math and science.
                </p>
              </>
            ),
          },
        ],
        // ---- CHAPTER 6: SUMMARY ----
        6: [
          {
            title: <h1>Summary & Next Steps</h1>,
            content: (
              <>
                <p>Excellent! You’ve covered all the Grade 6 topics.</p>
                <p>
                  A final quiz of 50 questions is available if you want to test
                  everything you’ve learned.
                </p>
                <p>
                  <strong>Next Steps:</strong> Try a mini-project—like a pretend
                  shop where you use ratios for mixing, decimals/percents for pricing,
                  and integer ops for inventory.
                </p>
                <p>
                  <strong>Looking Ahead:</strong> These skills are the foundation for
                  Grade 7 math—where you’ll dive into pre-algebra, data analysis,
                  and basic proofs.
                </p>
              </>
            ),
          }
        ]
      },
    7: {
        1: [
          {
            title: <h1>Rational Numbers: Introduction</h1>,
            content: (
              <>
                <p>
                  <strong>Rational numbers</strong> are numbers that can be expressed
                  as a fraction <code>p/q</code> where <code>p</code> and <code>q</code> are integers
                  and <code>q ≠ 0</code>. This set includes positive and negative fractions, mixed
                  numbers, and integers (which can be seen as fractions with denominator 1).
                </p>
                <p>
                  You’ll need to handle operations on rational numbers, including negative fractions,
                  and convert between fractions, decimals, and percents.
                </p>
              </>
            )
          },
          {
            title: <h1>Rational Numbers: Operations</h1>,
            content: (
              <>
                <p>
                  Key tasks with rational numbers:
                  <ul>
                    <li>
                      <strong>Adding/Subtracting Fractions:</strong> Find a common denominator, then
                      add/subtract numerators.
                    </li>
                    <li>
                      <strong>Multiplying Fractions:</strong> Multiply numerators and denominators
                      directly, then simplify.
                    </li>
                    <li>
                      <strong>Dividing Fractions:</strong> Multiply by the reciprocal (e.g.,
                      3/4 ÷ 2/5 = 3/4 × 5/2).
                    </li>
                  </ul>
                </p>
                <p>
                  Below is a small interactive fraction add/subtract tool. Try some negative fractions
                  too!
                </p>
              </>
            ),
            isRationalInteractive: true // your interactive tool
          }
        ],
        2: [
          {
            title: <h1>Multi-Step Equations: Review</h1>,
            content: (
              <>
                <p>
                  In Grade&nbsp;6, you handled simpler <strong>1-step</strong> or <strong>2-step</strong>{" "}
                  equations (like <code>x + 5 = 12</code> or <code>3x - 1 = 8</code>).
                  Now, at Grade&nbsp;7, <strong>multi-step</strong> means you might:
                </p>
                <ul>
                  <li><strong>Distribute</strong> across parentheses (e.g. multiply each term inside).</li>
                  <li>
                    <strong>Combine like terms</strong> on the same side (e.g. <code>2x + 3x</code>{" "}
                    becomes <code>5x</code>).
                  </li>
                  <li>
                    <strong>Move variables</strong> to one side and constants to the other (you can
                    add/subtract x terms on both sides).
                  </li>
                </ul>
                <p>Here’s a classic example. We start with:</p>
                <pre style={{ background: "#f5f5f5", padding: "8px" }}>
                  2(x - 3) + 1 = x + 4
                </pre>
                <ol>
                  <li><strong>Distribute</strong>: Multiply 2 by everything in (x - 3)</li>
                  <pre>2x - 6 + 1 = x + 4</pre>
                  <li><strong>Combine like terms</strong>: -6 + 1 = -5</li>
                  <pre>2x - 5 = x + 4</pre>
                  <li><strong>Subtract x</strong> from both sides to move all x’s to one side</li>
                  <pre>x - 5 = 4</pre>
                  <li><strong>Add 5</strong> to both sides to isolate x</li>
                  <pre>x = 9</pre>
                </ol>
                <p>
                  That final answer is <code>x = 9</code>. Multi-step equations can also include fractional coefficients, negative numbers, or variables on both sides. You’ll see more examples in the next page!
                </p>
              </>
            )
          },
          {
            title: <h1>Multi-Step Equations: Practice</h1>,
            content: (
              <>
                <p>
                  Here’s a random <strong>equation generator</strong> where you can test your
                  skills. Try solving for <code>x</code>, then click “Check” to see if
                  you’re correct.
                </p>
                <p>
                  You might encounter fractional coefficients or negative numbers, so watch
                  out for distributing and combining terms carefully!
                </p>
                <EquationGenerator />
              </>
            )
          }
        ],
        3: [
          {
            title: <h1>Proportions & Unit Rates: Introduction</h1>,
            content: (
              <>
                <p>
                  A <strong>proportion</strong> is an equation that states two ratios
                  are equal. For example, <code>2/3 = 4/6</code>. When two ratios are
                  proportional, you can use <em>cross multiplication</em> to find a
                  missing value. That skill is valuable for:
                </p>
                <ul>
                  <li><strong>Scaling recipes</strong> (e.g., doubling ingredients).</li>
                  <li><strong>Scale maps or diagrams</strong> (1 inch on a map = 100 miles in real life).</li>
                  <li>Many other <strong>real-life</strong> problems where one quantity changes at the same rate as another.</li>
                </ul>
                <p>
                  A <strong>unit rate</strong> compares some quantity to <em>1</em>. For
                  instance, <code>$2 per apple</code> is a unit rate. If 5 apples cost
                  $10, the cost per apple (the unit rate) is $2.
                </p>
                <p>
                  In all these situations, a unit rate helps us understand “how much per 1,”
                  making it easier to compare or scale up/down.
                </p>
              </>
            )
          },
          {
            title: <h1>Proportions: Scale Drawings & More</h1>,
            content: (
              <>
                <p>
                  Scale drawings use a ratio between the real object and a diagram.
                  For example, a scale of <code>1:26</code> means 1 inch on the drawing
                  represents 26 inches in real life. So if the drawing shows <em>2 inches</em>,
                  the real object would be <em>2 × 26 = 52 inches</em> long.
                </p>
                <p>
                  In general, <strong>multiply the drawing length by the scale factor</strong>{" "}
                  to find the real length.
                </p>
                <p>Below is a “Proportion Bar” interactive ...</p>
                <ProportionBar />
              </>
            )
          }
        ],
        4: [
          {
            title: <h1>Geometry: Circles</h1>,
            content: (
              <>
                <p>
                  In Grade 7, we revisit the key formulas for a circle:
                </p>
                <ul>
                  <li><strong>Area</strong>: <code>π × r²</code></li>
                  <li><strong>Circumference</strong>: <code>2 × π × r</code></li>
                </ul>
                <p>
                  Remember, <code>r</code> is the radius – half the diameter. So if a
                  circle’s diameter is 10, the radius is 5. If the radius is 3, then the
                  diameter is 6, and so on.
                </p>
                <p>
                  Below, you might include an illustration showing a circle with radius,
                  diameter, or a quick visual of how area/circumference relate.
                </p>
                <CircleTool />
              </>
            )
          },
          {
            title: <h1>Geometry: Polygons & Angles</h1>,
            content: (
              <>
                <p>
                  Polygons include <em>triangles, quadrilaterals</em>, and more. Key facts:
                </p>
                <ul>
                  <li><strong>Perimeter</strong>: add the lengths of all sides.</li>
                  <li>
                    <strong>Angle Sums</strong>:
                    <ul>
                      <li>A triangle’s angles add to 180°.</li>
                      <li>A quadrilateral’s angles add to 360°.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Special Quadrilaterals</strong>: squares, rectangles, parallelograms, trapezoids, etc.
                  </li>
                </ul>
                <p>
                  Below is a small interactive triangle. Drag its vertices to change side lengths
                  and angles, and see how the perimeter and angle measures update.
                </p>
                <PolygonTool />
              </>
            )
          },
          {
            title: <h1>Geometry: Transformations</h1>,
            content: (
              <>
                <p>
                  On the coordinate plane, you can <strong>translate</strong> (slide) a
                  figure, <strong>reflect</strong> (flip) it across an axis, and possibly{" "}
                  <strong>rotate</strong> or <strong>dilate</strong> (scale) it, depending
                  on your curriculum.
                </p>
                <p>
                  Here's an interactive tool that shows a simple triangle in the plane. You can
                  choose a transformation from the dropdown to see how the shape changes.
                </p>
                <TransformationTool />
                <p style={{ marginTop: "1em" }}>
                  You can still try plotting your own points in the normal coordinate plane below,
                  but the tool above gives you a live example of each transformation.
                </p>
              </>
            ),
            isGeometryInteractive: true
          },
          {
            title: <h1>Geometry: Surface Area & Volume</h1>,
            content: (
              <>
                <p>
                  Extend your knowledge to <strong>3D solids</strong>:
                </p>
                <ul>
                  <li>Surface Area of prisms & cylinders</li>
                  <li>Volume of various 3D shapes, e.g., rectangular prism: <code>V = l×w×h</code></li>
                </ul>
                <p>
                  Try the “3D shape” explorer below or measure a shape’s sides to
                  calculate volume or surface area.
                </p>
                <RectPrismTool />
              </>
            )
          }
        ],
        5: [
          {
            title: <h1>Probability: Simple & Compound Events</h1>,
            content: (
              <>
                <p>
                  A probability is a fraction that tells us how likely something is.
                  For one event it’s simply:
                  <br />
                  <code>P(event) = # favorable outcomes / # total outcomes</code>
                </p>
                <h4 style={{ marginTop: "1rem" }}>Compound events (independent)</h4>
                <p>
                  If two events don’t affect each other, multiply their
                  probabilities:
                  <br />
                  <code>P(A and B) = P(A) × P(B)</code>
                </p>
                <ul>
                  <li>Two heads in a row: <code>½ × ½ = ¼</code></li>
                  <li>Roll a 3 then a 5: <code>1⁄6 × 1⁄6 = 1⁄36</code></li>
                  <li>
                    Flip heads <em>and</em> roll an even number:
                    <code>½ × 3⁄6 = 1⁄6</code>
                  </li>
                </ul>
                <p style={{ fontSize: "0.9rem" }}>
                  Events like drawing two cards without replacement are called
                  <em> dependent</em>; their totals change after the first draw.
                  We’ll explore those next year!
                </p>
                <h4 style={{ marginTop: "1.2rem" }}>Try it yourself ▸</h4>
                <p>
                  Use the simulator below to flip coins or roll dice again and again.
                  Watch how the <strong>experimental</strong> results get closer to the
                  <strong> theoretical</strong> values as you collect more trials.
                </p>
                <CoinDiceSimulator />
              </>
            )
          },
          {
            title: <h1>Statistics Basics</h1>,
            content: (
              <>
                <p>
                  <strong>Statistics</strong> helps us collect, organize, analyze, and interpret data.
                  When dealing with numbers (like test scores, survey responses, or measurements), four
                  key measures give a quick snapshot of how the data is distributed:
                </p>
                <ul>
                  <li>
                    <strong>Mean</strong> – often called the “average.” To find it, you sum all data
                    values and divide by how many values you have. This can be influenced by extremely
                    high or low (outlier) values, so watch out for data sets where a few points might
                    skew the mean.
                  </li>
                  <li>
                    <strong>Median</strong> – the <em>middle</em> value once the data is sorted. If the
                    number of data points is even, you take the average of the two middle values. The
                    median often gives a sense of the “middle” that’s not as sensitive to outliers as
                    the mean is.
                  </li>
                  <li>
                    <strong>Mode</strong> – the most frequent value. A data set can have no mode (if all
                    values are unique), one mode, or multiple modes (if two or more values tie for the
                    highest frequency). Knowing the mode is useful for spotting what’s most common.
                  </li>
                  <li>
                    <strong>Range</strong> – the difference between the highest and lowest values in
                    the data set. This indicates how spread out the data is overall. A large range
                    suggests wide variability; a small range suggests the data is clustered.
                  </li>
                </ul>
                <p>
                  By examining these measures together, you can quickly see how your data is spread
                  out, what’s most common, and whether there are outliers. For example, two classes
                  might have the same average quiz score (the same mean), but one class’s scores
                  could be bunched close together (small range, small standard deviation) while the
                  other class’s scores vary widely (large range).
                </p>
                <p>
                  Below, you can try generating a random data set and compare the <strong>mean</strong>
                  {" "}to the <strong>median</strong>, see whether there’s a <strong>mode</strong>, and
                  check the <strong>range</strong>. Notice how changing just a few values might not
                  change the mode (most frequent), but can drastically shift the mean or the range.
                </p>
                <DataAnalyzer />
                <p>
                  Keep in mind that different data sets can have the same mean but very different
                  distributions. That’s why we often look at all these measures <em>together</em>—the
                  mean alone doesn’t tell the whole story!
                </p>
              </>
            ),
            isProbInteractive: true
          }
        ],
        6: [
          {
            title: <h1>Summary</h1>,
            content: (
              <>
                <p>
                  Great job exploring these Grade 7 topics: Rational Numbers,
                  Multi-Step Equations, Proportions, Geometry, and Probability.
                </p>
                <p>
                  You can now take a final quiz of 50 questions spanning all chapters.
                </p>
              </>
            )
          }
        ]
      },
      
    // In the future, just add 2: [...], 3: [...], ... 12: [...]
  };
  
  // Example: export a grade constant (for context, if you want)
  export const GRADE = 1;
  